import {
  Box,
  Button,
  Card,
  FormControlLabel,
  FormGroup,
  Switch,
  Typography,
} from "@mui/material";
import "./modification.css";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import {
  deleteFormRequest,
  downloadOrderResultRequest,
  generateFormRequest,
  updateQCModificationRequest,
  uploadFormRequest,
} from "../../../../utils/APICalls";
import Loading from "../../../../components/loading/loading";
import { getQCModificationRequest } from "../../../../redux/orders/slice";
import ErrorNotification from "../../../../components/error-notification/error-notification";
import ExcelIcon from "../../../../assets/Excel_Icon.svg";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import DeleteDialog from "../../../../components/delete-dialog/delete-dialog";

export default function ModifyInspection({
  orderId,
  orderNumber,
  isAllAnsweredApproved,
}: any) {
  const dispatch = useDispatch();
  const [isModification, setIsModification] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const { qcModification } = useSelector((state: any) => state.orders);

  useEffect(() => {
    if (qcModification) {
      setIsModification(qcModification?.isQcModificationRequired);
    }
  }, [qcModification]);

  const handleModificationChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const modification = event.target.checked;
    const payload = {
      isQcModificationRequired: modification,
    };
    updateQCModication(payload);
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setShowErrorNotification(false);
    const file = event.target.files?.[0];

    if (!file) return;

    const allowedExtensions = [".xlsx", ".xlsm"];
    const extension = "." + file.name.split(".").pop()?.toLowerCase();
    if (!allowedExtensions.includes(extension)) {
      setShowErrorNotification(true);
      setErrorMessage(
        "Unsupported file type. Only .xlsx or .xlxm files are allowed."
      );
      return;
    }

    if (!isFileSizeValid(file)) {
      return;
    }

    uploadFile(file);
  };

  const isFileSizeValid = (file: any) => {
    if (file?.size > 100000000) {
      setShowErrorNotification(true);
      setErrorMessage("File size exceeds 100MB limit.");
      return false;
    }
    return true;
  };

  const updateQCModication = async (payload: any) => {
    setIsLoading(true);
    const response = await updateQCModificationRequest(orderId, payload);
    setIsLoading(false);
    if (response?.status === 204) {
      dispatch(getQCModificationRequest({ orderId }));
    } else if (response?.status === 400 || response?.status === 403) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const onGenerateClick = async () => {
    setIsLoading(true);
    try {
      const response = await generateFormRequest(orderId);
      if (response && response.status === 200) {
        const blob = new Blob([response.data], {
          type: "application/zip",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${orderNumber}_Result`;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else if (response && response.status >= 400) {
        setShowErrorNotification(true);
        setErrorMessage("Failed to download document. Please try again.");
      }
    } catch (error) {
      setShowErrorNotification(true);
      setErrorMessage("Failed to download document. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const uploadFile = async (file: File) => {
    setIsLoading(true);
    const response = await uploadFormRequest(orderId, file);
    setIsLoading(false);
    if (response?.status === 204) {
      dispatch(getQCModificationRequest({ orderId }));
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response.data?.detail);
    }
  };

  const onDeleteFile = async () => {
    setIsLoading(true);
    const response = await deleteFormRequest(orderId);
    setIsLoading(false);
    if (response?.status === 204) {
      dispatch(getQCModificationRequest({ orderId }));
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response.data?.detail);
    }
  };

  const onDownloadClick = async () => {
    setIsLoading(true);
    try {
      const response = await downloadOrderResultRequest(orderId);
      if (response && response.status === 200) {
        const mimeType =
          response.headers["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        const blob = new Blob([response.data], {
          type: mimeType,
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${orderNumber}_Result`;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else if (response && response.status >= 400) {
        setShowErrorNotification(true);
        setErrorMessage("Failed to download document. Please try again.");
      }
    } catch (error) {
      setShowErrorNotification(true);
      setErrorMessage("Failed to download document. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Card className="card-layout">
        <Box className="alert-layout">
          <WarningAmberOutlinedIcon color="primary" />
          <Typography className="alert-txt">
            Some forms require images and image descriptions to be inserted in
            specific places inside the the completed report. When required, you
            will see the guided steps required for completion below.
          </Typography>
        </Box>
        <Box className="display-name-layout">
          <Typography className="label-txt">Modifications Required?</Typography>
          <Box width={"100%"}>
            <FormGroup sx={{ width: 80 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isModification}
                    onChange={handleModificationChange}
                  />
                }
                label={isModification ? "Yes" : "No"}
              />
            </FormGroup>

            {isModification && (
              <>
                <Box sx={{ display: "flex", mt: 3 }}>
                  <Box className="step-icon">
                    <Typography color="primary" fontSize={12}>
                      1
                    </Typography>
                  </Box>
                  <Box ml={1}>
                    <Typography fontWeight={"550"}>Complete Review</Typography>
                    <Typography>
                      Fully complete the QC review process. This must be
                      completed to enable following steps. For reviews that
                      require a 2nd QC, this will have to be done AFTER QC 2
                      review is complete.
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", mt: 2 }}>
                  <Box className="step-icon">
                    <Typography color="primary" fontSize={12}>
                      2
                    </Typography>
                  </Box>
                  <Box ml={1}>
                    <Typography fontWeight={"550"}>
                      Generate & Download Package
                    </Typography>
                    <Typography>
                      This process involves populating the data into the excel
                      form file, and creating a .ZIP file with the images to be
                      included. This may take a few moments.
                    </Typography>
                    <Button
                      sx={{ ml: 1, mt: 1 }}
                      variant="outlined"
                      startIcon={<DownloadOutlinedIcon />}
                      disabled={!isAllAnsweredApproved}
                      onClick={onGenerateClick}
                    >
                      Generate & Download
                    </Button>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", mt: 2 }}>
                  <Box className="step-icon">
                    <Typography color="primary" fontSize={12}>
                      3
                    </Typography>
                  </Box>
                  <Box ml={1}>
                    <Typography fontWeight={"550"}>
                      Modify & Save File
                    </Typography>
                    <Typography>
                      Make modifications to the excel file by adding images and
                      descriptions and save changes to your newly modified file.
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", mt: 2 }}>
                  <Box className="step-icon">
                    <Typography color="primary" fontSize={12}>
                      4
                    </Typography>
                  </Box>
                  <Box ml={1} width={"100%"}>
                    <Typography fontWeight={"550"}>Upload</Typography>
                    <Typography>
                      Upload your newly modified excel file.
                    </Typography>
                    {qcModification?.fileName ? (
                      <Box
                        className="document-data-content"
                        sx={{ width: "calc(100% - 40px)", mt: 1 }}
                      >
                        <Box display={"flex"}>
                          <img
                            src={ExcelIcon}
                            alt="Excel-icon"
                            height={40}
                            width={40}
                          />
                          <Box sx={{ ml: 1 }}>
                            <Typography>{qcModification?.fileName}</Typography>
                            <Typography
                              sx={{ fontSize: 14, color: "rgba(0, 0, 0, 0.6)" }}
                            >
                              Complete
                            </Typography>
                          </Box>
                        </Box>
                        <Box display={"flex"}>
                          <Button
                            variant="text"
                            startIcon={<DeleteOutlinedIcon />}
                            color="error"
                            onClick={() => setShowDeleteDialog(true)}
                          >
                            Delete
                          </Button>
                          <Button
                            sx={{ ml: 3 }}
                            variant="outlined"
                            startIcon={<DownloadOutlinedIcon />}
                            onClick={onDownloadClick}
                          >
                            Download
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box position={"relative"} width={130}>
                        <Button
                          sx={{ ml: 1, mt: 1 }}
                          variant="contained"
                          startIcon={<UploadOutlinedIcon />}
                          disabled={!isAllAnsweredApproved}
                        >
                          Upload
                        </Button>
                        <input
                          style={{
                            display: "block",
                            height: "100%",
                            width: "100%",
                            position: "absolute",
                            top: 0,
                            right: 0,
                            bottom: 0,
                            left: 0,
                            opacity: 0,
                            cursor: "pointer",
                          }}
                          type="file"
                          accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12"
                          onChange={handleFileChange}
                          name="form"
                          onClick={(event) => {
                            // same file selection issue
                            (event.target as HTMLInputElement).value = "";
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: "flex", mt: 2 }}>
                  <Box className="step-icon">
                    <Typography color="primary" fontSize={12}>
                      5
                    </Typography>
                  </Box>
                  <Box ml={1}>
                    <Typography fontWeight={"550"}>Submit</Typography>
                    <Typography>
                      Submit the order by clicking the “Submit” button in the
                      upper right.
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Card>
      {showDeleteDialog && (
        <DeleteDialog
          show={showDeleteDialog}
          close={() => setShowDeleteDialog(false)}
          message="Are you sure to delete this file."
          onConfirm={onDeleteFile}
        />
      )}
      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          onClose={() => setShowErrorNotification(false)}
          title={errorMessage}
        />
      )}
      {isLoading && <Loading />}
    </>
  );
}
